import type { Listing } from '@thevillages/schemas';

import '@tailwindplus/elements'; // Instantiate Tailwind Plus web components.
import { isPlatformBrowser } from '@angular/common';
import {
  afterNextRender,
  computed,
  DestroyRef,
  effect,
  inject,
  Injectable,
  PLATFORM_ID,
  signal,
} from '@angular/core';
import esriConfig from '@arcgis/core/config';

import type { PostMessageType } from '../../types/post-message-type';
import type { ToastMessage } from '../../types/toast-message';

// import { AddQueryParams } from '../../utils/add-query-params';
import { Environment } from '../../../environments/environment';

@Injectable({
  providedIn: 'root',
})
export class StateService {
  protected readonly platformId = inject(PLATFORM_ID);

  private readonly _destroyRef = inject(DestroyRef);

  public readonly allListingIds = signal<{ id: string; modelId: string | undefined }[]>([]);
  public authPromiseRejecter?: () => void;
  public readonly community = signal(Environment.community);
  public readonly communityDisplayName = computed(() =>
    this.community() === 'middleton' ? 'Middleton' : 'The Villages',
  );
  public handleAuthPromise = (event: MessageEvent): void => {
    const _token = (event.data as undefined | { data?: { token?: string } })?.data?.token;
    const _origin = event.origin;

    if (this.targetOrigins.includes(_origin) && !this.parentOrigin()) {
      this.parentOrigin.set(_origin);
    }

    if (_token) {
      this.isLoading.set(true);
      this.userToken.set(_token);
      sessionStorage.setItem('tv_auth_token', _token);

      this._authPromiseResolver?.();

      this.notifyParentOfStateChange('HF_EVENT:AUTH_ACCEPTED');
      this.isAuthModalOpen.set(false);

      setTimeout(() => {
        this.isLoading.set(false);
      }, 2000);
    } else {
      this.authPromiseRejecter?.();
      this.isAuthModalOpen.set(false);
    }
  };
  public readonly hoveredListing = signal<Listing | undefined>(undefined);
  public readonly initialListingsLoaded = signal(false);
  public readonly isAuthModalOpen = signal(false);
  public readonly isDarkMode = signal(false);
  public readonly isFiltersModalOpen = signal(false);
  public readonly isLoading = signal(false);
  public readonly isSaveSearchModalOpen = signal(false);
  public readonly isShareModalOpen = signal(false);
  public readonly isSidebarOpen = signal(false);
  public readonly mapViewCreated = signal(false);
  public readonly mobileMapView = signal(false);
  public readonly parentOrigin = signal('');
  public readonly selectedListingId = signal<string | undefined>(undefined);
  public readonly targetOrigins = [
    'https://0.0.0.0', // Common MAUI WebView origin
    'https://0.0.0.1', // Actual Android MAUI WebView origin (confirmed)
    'ms-appx-web://', // Windows MAUI origin
    'http://localhost:4200', // angular development origin
    'https://tvdevsite.wpenginepowered.com', // elementor plugin test dev
    'https://tvstgsite.wpenginepowered.com', // elementor plugin test stg
    'https://app.v3.thevillages.com', // staging origin
  ];
  public readonly toastMessage = signal<ToastMessage | undefined>(undefined);
  public readonly username = signal<string | undefined>(undefined);
  public readonly userToken = signal<string | undefined>(undefined);

  private _authPromiseResolver?: () => void;

  constructor() {
    afterNextRender(() => {
      // Set the Esri API key.
      esriConfig.apiKey = Environment.esriApiKey;

      // Set up listener for auth token messages from the parent window.
      window.addEventListener('message', (e: MessageEvent) => {
        if ((e.data as { type?: unknown }).type === 'AUTH_DATA') this.handleAuthPromise(e);
      });
      this._destroyRef.onDestroy(() => {
        window.removeEventListener('message', (e: MessageEvent) => {
          if ((e.data as { type?: unknown }).type === 'AUTH_DATA') this.handleAuthPromise(e);
        });
      });

      // Detect dark mode.
      // if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      //   this.isDarkMode.set(true);
      // };

      window.addEventListener('resize', () => {
        if (window.outerWidth > 1024) {
          this.mobileMapView.set(false);
        }
      });

      this._destroyRef.onDestroy(() => {
        window.removeEventListener('resize', () => {
          if (window.outerWidth > 1024) {
            this.mobileMapView.set(false);
          }
        });
      });

      const _storedToken = sessionStorage.getItem('tv_auth_token');
      const _storedUsername = sessionStorage.getItem('tv_username');

      if (_storedToken && _storedUsername) {
        this.username.set(_storedUsername);
        const _tokenObject = {
          data: { data: { token: _storedToken } },
        };
        setTimeout(() => {
          this.handleAuthPromise(_tokenObject as MessageEvent);
        }, 100);
      }

      // setTimeout(() => {
      //let parent application know we are ready. will allow initial auth handshake to occur and prevents potential race conditions
      this.notifyParentOfStateChange('HF_EVENT:READY');
      // }, 100);
    });

    effect(() => {
      if (!this.toastMessage()?.duration) {
        return;
      }

      // Clear toast message after specified duration.
      setTimeout(() => {
        this.toastMessage.set(undefined);
      }, this.toastMessage()?.duration);
    });
  }

  public authPromise(): Promise<void> {
    return new Promise<void>((resolve, reject) => {
      this._authPromiseResolver = resolve;
      this.authPromiseRejecter = reject;
    });
  }

  public handleAuthRequest(): void {
    if (Environment.isApp) {
      this.notifyParentOfStateChange('HF_EVENT:LOGIN_REQUIRED');
    } else {
      this.isAuthModalOpen.set(true);
    }
  }

  // async fetchSelectedListing(id: string) {
  //   const _base = `${environment.apiUrl}/listings`;
  //   const _url = AddQueryParams(_base, { ids: id, fields: 'address,baths,beds,certified,communities,description,features,furnished,flyerUrl,garage,geo,homesiteGeo,images,listingReps,mapImage,model,openToday,openTomorrow,pending,price,primaryImage,saleType,series,squareFeet,turnkey,uliKey,village,vls,willAccommodate,youtubeId' });
  //   const _options: RequestInit = {
  //     headers: {
  //       'Authorization': 'Basic 64f7873ca215d1eb1f72a13f',
  //       'Content-Type': 'application/json',
  //     },
  //   };
  //   const _results = await fetch(_url, _options);
  //   if (!_results.ok) { return undefined; }
  //   const _ids = await _results.json();
  //   if (!Array.isArray(_ids)) { return undefined; }
  //   return _ids[0] as Listing;
  // }
  public notifyParentOfStateChange(
    type: PostMessageType,
    data?: unknown,
    eventName?: string,
  ): void {
    if (!Environment.isApp) {
      return;
    }
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    if (type === 'HF_EVENT:READY') {
      setTimeout(() => {
        // On ready, set the parent origin so we can restrict postMessage calls to only that origin.
        // This usage is safe in our deployment context; see .semgrep-audit for details.
        // nosemgrep: javascript.browser.security.wildcard-postmessage-configuration.wildcard-postmessage-configuration
        window.parent.postMessage(type, '*');

        return;
      }, 100);
    }

    // if (!this.parentOrigin()) { return; }

    let messageData: {
      data?: unknown;
      eventName?: string;
      timestamp?: string;
      type: PostMessageType;
    } = { data, type };

    if (eventName) {
      messageData = { ...messageData, eventName, timestamp: new Date().toISOString() };
    }

    window.parent.postMessage(messageData, '*');
  }
}
